import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BIOVIZ_STATS } from '@/config/env.config';
import { getAuth0Token } from '@/utils/auth0Token';
import { wrapBaseQueryResult } from './baseQueryWithAuth';
import urljoin from 'url-join';

// Custom baseQuery that handles CORS/Private Network Access errors gracefully
// Note: Browser console will still show CORS errors (they cannot be suppressed programmatically),
// but this ensures the error is handled gracefully in the application code
// 
// CORS errors occur when:
// - Requesting from an origin not in the API's allowed origins list
// - Common in local development (localhost not in allowed origins)
// - This is a browser security feature, not an application error
//
// 4xx errors are handled separately by wrapBaseQueryResult (redirects to /securitycheck)
const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
	const baseQuery = fetchBaseQuery({ 
		baseUrl: urljoin(API_BIOVIZ_STATS),
		prepareHeaders: async (headers) => {
			// Use Auth0 SDK to get access token (consistent with other APIs)
			try {
				const token = await getAuth0Token();
				if (token) {
					headers.set('authorization', `Bearer ${token}`);
				} else {
					console.warn('⚠️ biovizApi: No token available, request may fail');
				}
			} catch (error) {
				console.error('❌ biovizApi: Error getting token:', error);
			}
			return headers;
		}
	});
	
	const result = await baseQuery(args, api, extraOptions);

	// Handle 401/403 errors (redirect to /securitycheck) - same as other APIs
	wrapBaseQueryResult(result, args);

	// If there's an error, check if it's a CORS/network error and handle it gracefully
	if (result.error) {
		const error = result.error;
		
		// Check if it's a CORS, network, or Private Network Access error
		// CORS errors occur when the browser blocks the request before it reaches the server
		// This is expected in local development if localhost is not in allowed origins
		const isCorsOrNetworkError = 
			error.status === 'FETCH_ERROR' ||
			error.status === 'CUSTOM_ERROR' ||
			error.status === 0 ||
			(error.error && typeof error.error === 'string' && (
				error.error.includes('CORS') ||
				error.error.includes('blocked') ||
				error.error.includes('address space') ||
				error.error.includes('ERR_FAILED') ||
				error.error.includes('Failed to fetch')
			));
		
		if (isCorsOrNetworkError) {
			console.warn('⚠️ biovizApi: Request failed (CORS/network/redirect error), treating as unavailable', {
				error: error.message || error.error,
				status: error.status
			});
			return {
				error: {
					status: 'TIMEOUT',
					error: 'Bioviz service unavailable',
				},
			};
		}
		
		// For any other error (including 4xx/5xx), treat as timeout
		console.warn('⚠️ biovizApi: Request failed, treating as unavailable', {
			error: error.message || error.error,
			status: error.status
		});
		return {
			error: {
				status: 'TIMEOUT',
				error: 'Bioviz service unavailable',
			},
		};
	}
	
	return result;
};

export const biovizApi = createApi({
	reducerPath: 'biovizApi',
	baseQuery: baseQueryWithErrorHandling,
	tagTypes: ['data'],
	endpoints: (builder) => ({
		fetchBiovizSampleSets: builder.query({
			query: (projectId) => ({
				// baseQuery prepareHeaders handles authorization header automatically
				url: `projects?project_id=${projectId}`,
				method: 'GET'
			}),
			// Cache data for 60 seconds (1 minute) after it's no longer used
			keepUnusedDataFor: 60,
			// Refetch if data is older than 60 seconds (1 minute TTL)
			// This ensures data is refreshed after 1 minute regardless of usage
			refetchOnMountOrArgChange: 60
		}),
		fetchAllBiovizProjects: builder.query({
			query: () => ({
				// baseQuery prepareHeaders handles authorization header automatically
				url: 'projects',
				method: 'GET'
			}),
			// Cache data for 5 minutes after it's no longer used
			keepUnusedDataFor: 300,
			// Refetch if data is older than 5 minutes
			refetchOnMountOrArgChange: 300
		})
	}),
});

export const {
	useFetchBiovizSampleSetsQuery,
	useFetchAllBiovizProjectsQuery,
} = biovizApi;